import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const MIN_LOAD_TIME_MS = 1500;
const LOAD_UI = '#ffffff';
const BSOD_BG = '#031BB0';
const BSOD_HOLD_MS = 3200;

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100vh); }
`;

const fillBar = keyframes`
  0% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
`;

const flicker = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.98; }
`;

const Screen = styled.div`
  position: fixed;
  inset: 0;
  background-color: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  font-family: 'Courier New', 'Courier', 'Lucida Console', monospace;
  overflow: hidden;
`;

const Scanline = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.03) 50%,
    transparent 100%
  );
  animation: ${scanline} 3s linear infinite;
  pointer-events: none;
`;

const Content = styled.div`
  position: absolute;
  top: 75%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  animation: ${flicker} 0.15s ease-in-out infinite;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90%;
  height: 90%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  z-index: 0;
  pointer-events: none;
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 280px;
`;

const BarFrame = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.35);
  padding: 2px;
  width: 100%;
  height: 20px;
  background: rgba(0, 0, 0, 0.35);
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.5);
`;

const BarFill = styled.div`
  height: 100%;
  width: 100%;
  transform-origin: left center;
  background: repeating-linear-gradient(
    90deg,
    ${LOAD_UI},
    ${LOAD_UI} 2px,
    rgba(255, 255, 255, 0.55) 2px,
    rgba(255, 255, 255, 0.55) 4px
  );
  animation: ${fillBar} ${MIN_LOAD_TIME_MS}ms ease-out forwards;
`;

const Label = styled.span`
  color: ${LOAD_UI};
  font-size: 11px;
  letter-spacing: 0.1em;
`;

const ContinueBox = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.65);
  padding: 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const ContinueTitle = styled.span`
  color: ${LOAD_UI};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
`;

const ContinueOptions = styled.div`
  display: flex;
  gap: 2.5rem;
`;

const ContinueOption = styled.span`
  color: ${LOAD_UI};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.12em;
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.45)};
  transition: opacity 150ms ease-out;
  &:hover {
    opacity: 1;
  }
  &::before {
    content: '${({ $active }) => ($active ? '▶ ' : '')}';
  }
`;

const BsodScreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background-color: ${BSOD_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
`;

const BsodImage = styled.img`
  display: block;
  width: auto;
  height: auto;
  max-width: none;
  max-height: none;
  flex-shrink: 0;
`;

/**
 * Full-screen BSOD interstitial after "enter"; WinXP can mount underneath.
 */
export function PostEnterBsod({ onDone }) {
  useEffect(() => {
    const id = window.setTimeout(() => {
      onDone?.();
    }, BSOD_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [onDone]);

  return (
    <BsodScreen role="presentation">
      <BsodImage src="/bsod-enter-transition.png" alt="" />
    </BsodScreen>
  );
}

function LoadingScreen({ onEnter }) {
  const [readyForEntry, setReadyForEntry] = useState(false);
  const videoRef = useRef(null);

  // Force first frame to render immediately
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.load();
      v.currentTime = 0;
    }
  }, []);

  useEffect(() => {
    const start = Date.now();
    let docReady = document.readyState === 'complete';

    const checkComplete = () => {
      const elapsed = Date.now() - start;
      const minElapsed = elapsed >= MIN_LOAD_TIME_MS;
      if (docReady && minElapsed) {
        setReadyForEntry(true);
      }
    };

    if (document.readyState === 'complete') {
      docReady = true;
    } else {
      window.addEventListener('load', () => {
        docReady = true;
        checkComplete();
      });
    }

    const id = setInterval(checkComplete, 100);
    return () => clearInterval(id);
  }, []);

  const handleEnter = () => {
    if (!readyForEntry) return;
    onEnter?.();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (readyForEntry && e.key === 'Enter') handleEnter();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readyForEntry]);

  return (
    <Screen>
        <BackgroundVideo ref={videoRef} autoPlay loop muted playsInline src="/bermuda-enter.mp4" />
        <Scanline />
        <Content>
          <LoadingSection>
            {readyForEntry ? (
              <ContinueBox>
                <ContinueTitle>CONTINUE?</ContinueTitle>
                <ContinueOptions>
                  <ContinueOption
                    $active
                    onClick={e => {
                      e.stopPropagation();
                      handleEnter();
                    }}
                  >
                    YES
                  </ContinueOption>
                  <ContinueOption
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    NO
                  </ContinueOption>
                </ContinueOptions>
              </ContinueBox>
            ) : (
              <>
                <BarFrame>
                  <BarFill />
                </BarFrame>
                <Label>[ LOADING EXPERIENCE... ]</Label>
              </>
            )}
          </LoadingSection>
        </Content>
    </Screen>
  );
}

export default LoadingScreen;
